/**
 * 6.5 {{ }} 数据绑定引擎（借鉴 Appsmith）
 *
 * 在原有 {{user.xx}} / {{params.xx}} / {{data.资源.字段}} 基础上扩展：
 * - 完整表达式求值：`{{ this.isRequired ? this.text && this.text.length > 0 : true }}`
 * - `{{ this.xxx }}` 派生属性上下文（Widget 运行时属性包）
 * - 动作触发器绑定：`{{ showAlert('保存成功', 'info'); saveCustomer(); }}` 多语句动作序列
 *
 * 安全模型（禁用任意代码执行）：
 * 1. 词法/语法层面无 eval、无 new Function、无 with —— 纯 AST 解释执行
 * 2. 标识符根仅限 user / params / data / this；`window`、`globalThis`、
 *    `process`、`eval`、`require`、`document` 等一律报 unknown-identifier，
 *    在 resolveBinding 中保持原样（绝不执行）
 * 3. 函数调用仅允许白名单函数（funcs），属性上的函数调用（user.fn()）被拒绝
 * 4. 成员访问阻止 __proto__ / prototype / constructor，且结果若为函数则返回
 *    undefined，不暴露可调用对象
 * 5. 表达式嵌套深度与 token 数受限，防御病态输入
 */

export interface BindingContext {
  user?: Record<string, unknown>;
  params?: Record<string, string>;
  data?: Record<string, Record<string, unknown>[]>;
  /** 当前 Widget 的运行时属性包（default + meta + derived） */
  this?: Record<string, unknown>;
  /** 允许携带额外上下文（如 permissions），但不可作为表达式根 */
  [key: string]: unknown;
}

export type BindingFunction = (...args: unknown[]) => unknown;

export type BindingFunctions = Record<string, BindingFunction>;

export type BindingErrorCode =
  | "syntax"
  | "unknown-identifier"
  | "unknown-function"
  | "unsafe-member"
  | "member-call"
  | "depth"
  | "evaluation";

export class BindingError extends Error {
  code: BindingErrorCode;

  constructor(code: BindingErrorCode, message: string) {
    super(message);
    this.name = "BindingError";
    this.code = code;
  }
}

/** 禁止访问的成员名 */
const UNSAFE_MEMBERS = new Set(["__proto__", "prototype", "constructor"]);

const MAX_DEPTH = 64;
const MAX_TOKENS = 512;

// ------------------------------------------------------------
// Tokenizer
// ------------------------------------------------------------

type TokenType =
  | "number"
  | "string"
  | "identifier"
  | "operator"
  | "punct"
  | "eof";

interface Token {
  type: TokenType;
  value: string;
  pos: number;
}

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const len = source.length;

  const push = (type: TokenType, value: string, pos: number) => {
    if (tokens.length >= MAX_TOKENS) {
      throw new BindingError("depth", "binding expression too long");
    }
    tokens.push({ type, value, pos });
  };

  while (i < len) {
    const ch = source[i];

    if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
      i += 1;
      continue;
    }

    // 字符串字面量
    if (ch === "'" || ch === '"') {
      const quote = ch;
      const start = i;
      i += 1;
      let value = "";
      let closed = false;
      while (i < len) {
        const c = source[i];
        if (c === "\\" && i + 1 < len) {
          const next = source[i + 1];
          value += next === "n" ? "\n" : next === "t" ? "\t" : next;
          i += 2;
        } else if (c === quote) {
          closed = true;
          i += 1;
          break;
        } else {
          value += c;
          i += 1;
        }
      }
      if (!closed) {
        throw new BindingError("syntax", `unterminated string at ${start}`);
      }
      push("string", value, start);
      continue;
    }

    // 数字字面量（\d+(\.\d+)?：不贪婪吞掉作为成员访问的 "."）
    if (/[0-9]/.test(ch)) {
      const start = i;
      let num = "";
      while (i < len && /[0-9]/.test(source[i])) {
        num += source[i];
        i += 1;
      }
      if (i < len && source[i] === "." && i + 1 < len && /[0-9]/.test(source[i + 1])) {
        num += ".";
        i += 1;
        while (i < len && /[0-9]/.test(source[i])) {
          num += source[i];
          i += 1;
        }
      }
      push("number", num, start);
      continue;
    }

    // 标识符 / 关键字
    if (/[A-Za-z_$]/.test(ch)) {
      const start = i;
      let name = "";
      while (i < len && /[A-Za-z0-9_$]/.test(source[i])) {
        name += source[i];
        i += 1;
      }
      push("identifier", name, start);
      continue;
    }

    // 多字符运算符
    const three = source.slice(i, i + 3);
    if (three === "===" || three === "!==") {
      push("operator", three, i);
      i += 3;
      continue;
    }
    const two = source.slice(i, i + 2);
    if (["==", "!=", "<=", ">=", "&&", "||"].includes(two)) {
      push("operator", two, i);
      i += 2;
      continue;
    }

    // 单字符
    if ("+-*/%<>!".includes(ch)) {
      push("operator", ch, i);
      i += 1;
      continue;
    }
    if ("().,?:;[]".includes(ch)) {
      push("punct", ch, i);
      i += 1;
      continue;
    }

    throw new BindingError("syntax", `unexpected character '${ch}' at ${i}`);
  }

  tokens.push({ type: "eof", value: "", pos: len });
  return tokens;
}

// ------------------------------------------------------------
// Parser（递归下降）
// ------------------------------------------------------------

interface LiteralNode {
  type: "literal";
  value: unknown;
}

interface IdentifierNode {
  type: "identifier";
  name: string;
}

interface MemberNode {
  type: "member";
  object: ExprNode;
  property: ExprNode;
}

interface CallNode {
  type: "call";
  callee: IdentifierNode;
  args: ExprNode[];
}

interface BinaryNode {
  type: "binary";
  op: string;
  left: ExprNode;
  right: ExprNode;
}

interface UnaryNode {
  type: "unary";
  op: string;
  operand: ExprNode;
}

interface LogicalNode {
  type: "logical";
  op: "&&" | "||";
  left: ExprNode;
  right: ExprNode;
}

interface ConditionalNode {
  type: "conditional";
  test: ExprNode;
  consequent: ExprNode;
  alternate: ExprNode;
}

interface SequenceNode {
  type: "sequence";
  expressions: ExprNode[];
}

type ExprNode =
  | LiteralNode
  | IdentifierNode
  | MemberNode
  | CallNode
  | BinaryNode
  | UnaryNode
  | LogicalNode
  | ConditionalNode
  | SequenceNode;

const KEYWORDS: Record<string, unknown> = {
  true: true,
  false: false,
  null: null,
  undefined: undefined,
};

class Parser {
  private tokens: Token[];
  private index = 0;

  constructor(source: string) {
    this.tokens = tokenize(source);
  }

  private peek(): Token {
    return this.tokens[this.index];
  }

  private next(): Token {
    const token = this.tokens[this.index];
    if (token.type !== "eof") this.index += 1;
    return token;
  }

  private matchPunct(p: string): boolean {
    const t = this.peek();
    if (t.type === "punct" && t.value === p) {
      this.index += 1;
      return true;
    }
    return false;
  }

  private expectPunct(p: string): void {
    if (!this.matchPunct(p)) {
      throw new BindingError("syntax", `expected '${p}' at ${this.peek().pos}`);
    }
  }

  parse(): ExprNode {
    const expr = this.parseSequence();
    const t = this.peek();
    if (t.type !== "eof") {
      throw new BindingError("syntax", `unexpected token '${t.value}' at ${t.pos}`);
    }
    return expr;
  }

  private parseSequence(): ExprNode {
    const first = this.parseTernary();
    const expressions = [first];
    while (this.peek().type === "punct" && this.peek().value === ";") {
      this.next();
      if (this.peek().type === "eof") break;
      expressions.push(this.parseTernary());
    }
    if (expressions.length === 1) return expressions[0];
    return { type: "sequence", expressions };
  }

  private parseTernary(): ExprNode {
    const test = this.parseLogical();
    if (this.matchPunct("?")) {
      const consequent = this.parseTernary();
      this.expectPunct(":");
      const alternate = this.parseTernary();
      return { type: "conditional", test, consequent, alternate };
    }
    return test;
  }

  private parseLogical(): ExprNode {
    let left = this.parseEquality();
    for (;;) {
      if (this.peek().type === "operator" && (this.peek().value === "&&" || this.peek().value === "||")) {
        const op = this.next().value as "&&" | "||";
        const right = this.parseEquality();
        left = { type: "logical", op, left, right };
      } else {
        break;
      }
    }
    return left;
  }

  private parseEquality(): ExprNode {
    let left = this.parseRelational();
    for (;;) {
      const t = this.peek();
      if (t.type === "operator" && ["===", "!==", "==", "!="].includes(t.value)) {
        this.next();
        const right = this.parseRelational();
        left = { type: "binary", op: t.value, left, right };
      } else {
        break;
      }
    }
    return left;
  }

  private parseRelational(): ExprNode {
    let left = this.parseAdditive();
    for (;;) {
      const t = this.peek();
      if (t.type === "operator" && ["<", "<=", ">", ">="].includes(t.value)) {
        this.next();
        const right = this.parseAdditive();
        left = { type: "binary", op: t.value, left, right };
      } else {
        break;
      }
    }
    return left;
  }

  private parseAdditive(): ExprNode {
    let left = this.parseMultiplicative();
    for (;;) {
      const t = this.peek();
      if (t.type === "operator" && (t.value === "+" || t.value === "-")) {
        this.next();
        const right = this.parseMultiplicative();
        left = { type: "binary", op: t.value, left, right };
      } else {
        break;
      }
    }
    return left;
  }

  private parseMultiplicative(): ExprNode {
    let left = this.parseUnary();
    for (;;) {
      const t = this.peek();
      if (t.type === "operator" && ["*", "/", "%"].includes(t.value)) {
        this.next();
        const right = this.parseUnary();
        left = { type: "binary", op: t.value, left, right };
      } else {
        break;
      }
    }
    return left;
  }

  private parseUnary(): ExprNode {
    const t = this.peek();
    if (t.type === "operator" && (t.value === "!" || t.value === "-")) {
      this.next();
      return { type: "unary", op: t.value, operand: this.parseUnary() };
    }
    return this.parsePostfix();
  }

  private parsePostfix(): ExprNode {
    let expr = this.parsePrimary();
    for (;;) {
      if (this.matchPunct(".")) {
        const name = this.peek();
        // 兼容旧语法：data.customers.0.name 中 ".0" 为数字下标
        if (name.type === "identifier") {
          this.next();
          expr = {
            type: "member",
            object: expr,
            property: { type: "literal", value: name.value },
          };
        } else if (name.type === "number") {
          this.next();
          expr = {
            type: "member",
            object: expr,
            property: { type: "literal", value: Number(name.value) },
          };
        } else {
          throw new BindingError("syntax", `expected property name at ${name.pos}`);
        }
      } else if (this.matchPunct("[")) {
        const property = this.parseTernary();
        this.expectPunct("]");
        expr = { type: "member", object: expr, property };
      } else {
        break;
      }
    }
    return expr;
  }

  private parsePrimary(): ExprNode {
    const t = this.peek();

    if (t.type === "number") {
      this.next();
      const value = Number(t.value);
      return { type: "literal", value: Number.isNaN(value) ? 0 : value };
    }

    if (t.type === "string") {
      this.next();
      return { type: "literal", value: t.value };
    }

    if (t.type === "identifier") {
      this.next();
      if (t.value in KEYWORDS) {
        return { type: "literal", value: KEYWORDS[t.value] };
      }
      // 函数调用（仅允许纯标识符 callee，白名单校验在解释器完成）
      if (this.peek().type === "punct" && this.peek().value === "(") {
        this.next();
        const args: ExprNode[] = [];
        if (!(this.peek().type === "punct" && this.peek().value === ")")) {
          args.push(this.parseTernary());
          while (this.matchPunct(",")) {
            args.push(this.parseTernary());
          }
        }
        this.expectPunct(")");
        return { type: "call", callee: { type: "identifier", name: t.value }, args };
      }
      return { type: "identifier", name: t.value };
    }

    if (t.type === "punct" && t.value === "(") {
      this.next();
      const expr = this.parseTernary();
      this.expectPunct(")");
      return expr;
    }

    throw new BindingError("syntax", `unexpected token '${t.value}' at ${t.pos}`);
  }
}

// ------------------------------------------------------------
// Interpreter（安全求值）
// ------------------------------------------------------------

function accessMember(base: unknown, key: unknown): unknown {
  if (base === undefined || base === null) return undefined;

  const memberName =
    typeof key === "number"
      ? String(key)
      : typeof key === "string"
        ? key
        : String(key);

  if (UNSAFE_MEMBERS.has(memberName)) {
    throw new BindingError("unsafe-member", `access to '${memberName}' is not allowed`);
  }

  if (typeof base === "function") return undefined;

  if (Array.isArray(base)) {
    if (memberName === "length") return base.length;
    if (!/^\d+$/.test(memberName)) return undefined;
    return base[parseInt(memberName, 10)];
  }

  if (typeof base === "object") {
    const value = (base as Record<string, unknown>)[memberName];
    // 不暴露可调用对象（函数不可作为属性链上被调用的值）
    if (typeof value === "function") return undefined;
    return value;
  }

  // 字符串等原始值：仅允许数值索引与 length
  if (typeof base === "string") {
    if (memberName === "length") return base.length;
    if (/^\d+$/.test(memberName)) return base[parseInt(memberName, 10)];
    return undefined;
  }

  return undefined;
}

function resolveRoot(name: string, ctx: BindingContext): unknown {
  // `this` 为派生属性上下文：未就绪时抛 unknown-identifier，使 resolveBinding
  // 保留原文（{{this.x}}），允许派生属性包构建后二次解析
  if (name === "this") {
    if (!ctx.this) {
      throw new BindingError("unknown-identifier", "`this` 上下文未就绪");
    }
    return ctx.this;
  }
  if (name === "user" || name === "params" || name === "data") {
    return ctx[name];
  }
  // 未识别标识符（window / eval / globalThis / process / 任意变量名）一律拒绝
  throw new BindingError("unknown-identifier", `unknown identifier '${name}'`);
}

function toNumber(value: unknown): number {
  if (typeof value === "number") return value;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
}

function toBool(value: unknown): boolean {
  return Boolean(value);
}

function evalNode(node: ExprNode, ctx: BindingContext, funcs: BindingFunctions, depth: number): unknown {
  if (depth > MAX_DEPTH) {
    throw new BindingError("depth", "binding expression too deeply nested");
  }

  switch (node.type) {
    case "literal":
      return node.value;

    case "identifier":
      return resolveRoot(node.name, ctx);

    case "member": {
      const base = evalNode(node.object, ctx, funcs, depth + 1);
      const key = evalNode(node.property, ctx, funcs, depth + 1);
      return accessMember(base, key);
    }

    case "call": {
      const fn = funcs[node.callee.name];
      if (typeof fn !== "function") {
        throw new BindingError(
          "unknown-function",
          `function '${node.callee.name}' is not in the whitelist`,
        );
      }
      const args = node.args.map((arg) => evalNode(arg, ctx, funcs, depth + 1));
      return fn(...args);
    }

    case "unary": {
      const value = evalNode(node.operand, ctx, funcs, depth + 1);
      if (node.op === "!") return !value;
      if (node.op === "-") return -toNumber(value);
      throw new BindingError("evaluation", `unsupported unary operator '${node.op}'`);
    }

    case "logical": {
      const left = evalNode(node.left, ctx, funcs, depth + 1);
      if (node.op === "&&") {
        return toBool(left) ? evalNode(node.right, ctx, funcs, depth + 1) : left;
      }
      return toBool(left) ? left : evalNode(node.right, ctx, funcs, depth + 1);
    }

    case "conditional": {
      const test = evalNode(node.test, ctx, funcs, depth + 1);
      return toBool(test)
        ? evalNode(node.consequent, ctx, funcs, depth + 1)
        : evalNode(node.alternate, ctx, funcs, depth + 1);
    }

    case "binary":
      return evalBinary(node.op, node.left, node.right, ctx, funcs, depth);

    case "sequence": {
      let result: unknown = undefined;
      for (const expr of node.expressions) {
        result = evalNode(expr, ctx, funcs, depth + 1);
      }
      return result;
    }
  }
}

function evalBinary(
  op: string,
  leftNode: ExprNode,
  rightNode: ExprNode,
  ctx: BindingContext,
  funcs: BindingFunctions,
  depth: number,
): unknown {
  // 逻辑短路由 logical 节点处理；&& / || 不会走到这里
  const left = evalNode(leftNode, ctx, funcs, depth + 1);
  const right = evalNode(rightNode, ctx, funcs, depth + 1);

  switch (op) {
    case "+": {
      if (typeof left === "string" || typeof right === "string") {
        return String(left) + String(right);
      }
      return toNumber(left) + toNumber(right);
    }
    case "-":
      return toNumber(left) - toNumber(right);
    case "*":
      return toNumber(left) * toNumber(right);
    case "/": {
      const divisor = toNumber(right);
      return divisor === 0 ? 0 : toNumber(left) / divisor;
    }
    case "%": {
      const divisor = toNumber(right);
      return divisor === 0 ? 0 : toNumber(left) % divisor;
    }
    case "===":
      return left === right;
    case "!==":
      return left !== right;
    case "==": {
      if (left === right) return true;
      if (left == null || right == null) return false;
      return toNumber(left) === toNumber(right);
    }
    case "!=": {
      if (left === right) return false;
      if (left == null || right == null) return true;
      return toNumber(left) !== toNumber(right);
    }
    case "<":
    case "<=":
    case ">":
    case ">=": {
      if (typeof left === "string" && typeof right === "string") {
        const a = left;
        const b = right;
        return op === "<" ? a < b : op === "<=" ? a <= b : op === ">" ? a > b : a >= b;
      }
      const a = toNumber(left);
      const b = toNumber(right);
      return op === "<" ? a < b : op === "<=" ? a <= b : op === ">" ? a > b : a >= b;
    }
    default:
      throw new BindingError("evaluation", `unsupported operator '${op}'`);
  }
}

// ------------------------------------------------------------
// 公共 API
// ------------------------------------------------------------

/**
 * 解析并求值单个绑定表达式（不带 {{ }} 包裹）。
 * 未知标识符/非白名单函数/危险成员访问会抛出 BindingError。
 */
export function evaluateExpression(
  source: string,
  ctx: BindingContext,
  funcs: BindingFunctions = DEFAULT_BINDING_FUNCTIONS,
): unknown {
  const parser = new Parser(source);
  const ast = parser.parse();
  return evalNode(ast, ctx, funcs, 0);
}

/** 仅解析，返回 AST（供测试/诊断） */
export function parseExpression(source: string): ExprNode {
  return new Parser(source).parse();
}

/** 动作序列中的一条语句 */
export interface ActionStatement {
  /** 语句源码 */
  source: string;
  /** 白名单函数名 */
  calleeName: string;
}

/**
 * 解析动作触发器序列：`showAlert('保存成功', 'info'); saveCustomer();`
 * 要求每条语句都是白名单函数调用；否则抛出 BindingError。
 */
export function parseActionSequence(source: string): ActionStatement[] {
  const parser = new Parser(source);
  const ast = parser.parse();

  const walk = (node: ExprNode): ActionStatement[] => {
    if (node.type === "sequence") {
      return node.expressions.flatMap(walk);
    }
    if (node.type === "call") {
      return [{ source: source, calleeName: node.callee.name }];
    }
    throw new BindingError(
      "evaluation",
      "action sequence statements must be whitelisted function calls",
    );
  };

  return walk(ast);
}

/** 依次执行动作序列中的每条语句，返回各语句结果（执行顺序保持声明顺序） */
export function executeActionSequence(
  source: string,
  ctx: BindingContext,
  funcs: BindingFunctions = DEFAULT_BINDING_FUNCTIONS,
): unknown[] {
  const parser = new Parser(source);
  const ast = parser.parse();

  if (ast.type === "sequence") {
    return ast.expressions.map((expr) => evalNode(expr, ctx, funcs, 0));
  }
  return [evalNode(ast, ctx, funcs, 0)];
}

/**
 * 判断一个 props 值是否为动作触发器绑定：
 * `{{ showAlert('ok'); saveCustomer(); }}` 这类多语句函数调用序列
 */
export function isActionSequenceBinding(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  const match = /^\{\{\s*([\s\S]*?)\s*\}\}$/.exec(trimmed);
  if (!match) return false;
  try {
    return parseActionSequence(match[1]).length >= 1;
  } catch {
    return false;
  }
}

/**
 * 解析字符串中的 {{ }} 绑定：
 * - 整串恰为一个绑定且非动作序列时，保留求值结果的原始类型（如布尔/数字）
 * - 混合字符串时做插值（String 化）
 * - 已知根（user/params/data/this）下路径未命中 -> `[表达式]`（保持旧行为）
 * - 未知标识符（如 window/eval）-> 保留 `{{ ... }}` 原文，绝不执行，
 *   允许后续 pass（如 this 上下文就绪后）重新解析
 */
export function resolveBinding(
  value: unknown,
  ctx: BindingContext,
  funcs: BindingFunctions = DEFAULT_BINDING_FUNCTIONS,
): unknown {
  if (typeof value !== "string") return value;

  // 动作触发器绑定（{{ fn(); fn(); }}）是触发器而非取值：保持原文，绝不在此求值执行
  if (isActionSequenceBinding(value)) return value;

  const matches = [...value.matchAll(/\{\{\s*([\s\S]*?)\s*\}\}/g)];
  if (matches.length === 0) return value;

  const trimmed = value.trim();
  const wholeMatch =
    matches.length === 1 && matches[0][0] === trimmed && trimmed.startsWith("{{");

  const evaluate = (expr: string): { resolved: boolean; value: unknown; raw: string } => {
    try {
      const result = evaluateExpression(expr, ctx, funcs);
      if (result === undefined) {
        return { resolved: false, value: undefined, raw: `[${expr}]` };
      }
      return { resolved: true, value: result, raw: `[${expr}]` };
    } catch (err) {
      if (err instanceof BindingError && err.code === "unknown-identifier") {
        // 根尚未就绪（如 this 上下文缺失）或非法标识符：保留原文，安全且可再求值
        return { resolved: false, value: undefined, raw: `{{ ${expr} }}` };
      }
      return { resolved: false, value: undefined, raw: `[${expr}]` };
    }
  };

  if (wholeMatch) {
    const expr = matches[0][1];
    const outcome = evaluate(expr);
    return outcome.resolved ? outcome.value : outcome.raw;
  }

  let output = value;
  for (const match of matches) {
    const expr = match[1];
    const outcome = evaluate(expr);
    output = output.replace(match[0], outcome.resolved ? String(outcome.value) : outcome.raw);
  }
  return output;
}

/** 批量解析 props（与 renderer 中 resolveProps 等价，增强为表达式求值） */
export function resolveProps(
  props: Record<string, unknown>,
  ctx: BindingContext,
  funcs: BindingFunctions = DEFAULT_BINDING_FUNCTIONS,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(props).map(([key, value]) => [key, resolveBinding(value, ctx, funcs)]),
  );
}

// ------------------------------------------------------------
// 默认白名单函数
// ------------------------------------------------------------

let alertHandler: ((message: string, type: string) => void) | null = null;

/** 注册全局 alert 处理器（运行时注入，如 window.alert / toast） */
export function setAlertHandler(handler: ((message: string, type: string) => void) | null): void {
  alertHandler = handler;
}

/**
 * 默认白名单函数集。showAlert / log 为副作用函数；其余为纯函数。
 * 渲染器可传入自定义 funcs 覆盖/扩展。
 */
export const DEFAULT_BINDING_FUNCTIONS: BindingFunctions = {
  showAlert: (message, type = "info") => {
    alertHandler?.(String(message), String(type));
    return undefined;
  },
  log: (...args) => {
    // eslint-disable-next-line no-console
    console.log("[binding]", ...args);
    return undefined;
  },
  now: () => new Date().toISOString(),
  round: (value) => Math.round(toNumber(value)),
  floor: (value) => Math.floor(toNumber(value)),
  ceil: (value) => Math.ceil(toNumber(value)),
  min: (...args) => Math.min(...args.map(toNumber)),
  max: (...args) => Math.max(...args.map(toNumber)),
  stringify: (value) => JSON.stringify(value),
};
